import React, { useEffect, useState } from 'react';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import PageHero from '../../components/PageHero.jsx';
import PriceTicker, { Disclaimer } from '../../components/PriceTicker.jsx';
import { loadPrices, loadPriceHistory, publicGet, metalName, formatMetalPrice, unitLabel, formatChange, formatUpdated, formatRate, formatDay } from '../../metalPrices.js';
import { useFxBase, baseOptions, crossRates } from '../../fxBase.js';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// /prices — every enabled metal with its price, day change and a 30-day
// line of daily closes (built up from our own stored prices), then the
// Bank of Canada exchange rates in the visitor's base currency.
export default function Prices() {
  const { t } = useI18n();
  usePageTitle('prices.title');
  const [data, setData] = useState(null);
  const [history, setHistory] = useState({});
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    loadPrices().then((d) => alive && setData(d)).catch(() => alive && setFailed(true));
    loadPriceHistory().then((h) => alive && setHistory(h)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const groups = data ? [['base', data.metals.filter((m) => m.kind === 'base')], ['precious', data.metals.filter((m) => m.kind === 'precious')]] : [];
  return (
    <>
      <PriceTicker />
      <PageHero title={t('prices.title')} lead={t('prices.lead')}><TutorialSlot place="site.prices" /></PageHero>
      <section className="section section-tight">
        <div className="container">
          {!data && !failed && <p className="muted">{t('common.loading')}</p>}
          {(failed || (data && !data.metals.length)) && <div className="empty-state"><p className="muted">{t('prices.none')}</p></div>}
          {groups.filter(([, list]) => list.length).map(([kind, list]) => (
            <div key={kind} className="prices-group">
              <h2>{t(`prices.groups.${kind}`)}</h2>
              <div className="prices-table" role="table" aria-label={t(`prices.groups.${kind}`)}>
                <div className="prices-row prices-head" role="row">
                  <span role="columnheader">{t('prices.cols.metal')}</span>
                  <span role="columnheader">{t('prices.cols.price')}</span>
                  <span role="columnheader">{t('prices.cols.change')}</span>
                  <span role="columnheader">{t('prices.cols.chart')}</span>
                  <span role="columnheader">{t('prices.cols.updated')}</span>
                </div>
                {list.map((m) => <PriceRow key={m.code} m={m} points={history[m.code] || []} />)}
              </div>
            </div>
          ))}
          {data && data.metals.length > 0 && <p className="prices-disclaimer"><Disclaimer data={data} /></p>}
          <FxSection />
        </div>
      </section>
    </>
  );
}

function PriceRow({ m, points }) {
  const { t, lang } = useI18n();
  const ch = formatChange(m, lang);
  return (
    <div className="prices-row" role="row">
      <span role="cell" className="prices-metal"><span className={`metal-dot metal-${m.code}`} aria-hidden="true" />{metalName(m, lang)}</span>
      <span role="cell" className="prices-price">{formatMetalPrice(m, lang)}<span className="muted small"> {unitLabel(m, t)}</span></span>
      <span role="cell">
        {ch ? <span className={`change-pill is-${ch.dir}`}><span aria-hidden="true">{ch.arrow}</span> {ch.text}</span> : <span className="muted small">{t('prices.noChange')}</span>}
      </span>
      <span role="cell"><Sparkline points={points} name={metalName(m, lang)} /></span>
      <span role="cell" className="muted small">{formatUpdated(m.priceAt, lang)}</span>
    </div>
  );
}

function Sparkline({ points, name }) {
  const { t, lang } = useI18n();
  if (points.length < 2) return <span className="muted small">{t('prices.historyBuilding')}</span>;
  const W = 140, H = 36, P = 3;
  const vals = points.map((p) => p.close);
  const min = Math.min(...vals), max = Math.max(...vals);
  const x = (i) => P + (i * (W - 2 * P)) / (points.length - 1);
  const y = (v) => (max === min ? H / 2 : P + (H - 2 * P) * (1 - (v - min) / (max - min)));
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.close).toFixed(1)}`).join(' ');
  const up = vals.at(-1) >= vals[0];
  const fmt = (v) => new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { maximumFractionDigits: v >= 1000 ? 0 : 2 }).format(v);
  const label = t('prices.chartLabel', { name, n: points.length, min: fmt(min), max: fmt(max) });
  return (
    <svg className={`sparkline ${up ? 'is-up' : 'is-down'}`} viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={label}>
      <title>{label}</title>
      <path d={`${d} L${x(points.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`} className="sparkline-area" />
      <path d={d} className="sparkline-line" />
      <circle cx={x(points.length - 1)} cy={y(vals.at(-1))} r="2.5" className="sparkline-dot" />
    </svg>
  );
}

// Exchange rates in the visitor's base currency (same choice as the strip,
// remembered in this browser). Cross rates from the Bank's CAD rates.
function FxSection() {
  const { t, lang } = useI18n();
  const [fx, setFx] = useState(null);
  const [base, setBase] = useFxBase();
  useEffect(() => {
    let alive = true;
    publicGet('/tickers/fx').then((d) => alive && setFx(d)).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!fx || !fx.items.length) return null;
  const options = baseOptions(fx.items);
  const b = options.includes(base) ? base : 'CAD';
  const rows = crossRates(fx.items, b);
  return (
    <div className="prices-group fx-section">
      <div className="fx-head">
        <h2>{t('prices.fxTitle')}</h2>
        <label className="fx-base-field">
          <span>{t('tickers.fx.baseLabel')}</span>
          <select value={b} onChange={(e) => setBase(e.target.value)}>
            {options.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <p className="muted small">{t('prices.fxLead', { base: b })}</p>
      <div className="prices-table" role="table" aria-label={t('prices.fxTitle')}>
        <div className="prices-row fx-row prices-head" role="row">
          <span role="columnheader">{t('prices.fxCols.pair')}</span>
          <span role="columnheader">{t('prices.fxCols.rate', { base: b })}</span>
          <span role="columnheader">{t('prices.cols.change')}</span>
          <span role="columnheader">{t('prices.fxCols.date')}</span>
        </div>
        {rows.map((r) => {
          const ch = formatChange(r, lang);
          return (
            <div key={r.code} className="prices-row fx-row" role="row">
              <span role="cell" className="prices-metal">{r.code}/{r.base}</span>
              <span role="cell" className="prices-price">{formatRate(r.rate, lang)} <span className="muted small">{r.base}</span></span>
              <span role="cell">{ch ? <span className={`change-pill is-${ch.dir}`}><span aria-hidden="true">{ch.arrow}</span> {ch.text}</span> : <span className="muted small">—</span>}</span>
              <span role="cell" className="muted small">{formatDay(r.date, lang)}</span>
            </div>
          );
        })}
      </div>
      <p className="prices-disclaimer">{t('tickers.fx.disclaimer', { date: formatDay(fx.date, lang) })}{b !== 'CAD' && ` ${t('tickers.fx.crossNote', { base: b })}`}</p>
    </div>
  );
}

